import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Card, Row, Col, Input, Modal, Form, InputNumber } from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { getProfitRateList, createProfitRate, updateProfitRate, deleteProfitRate } from '../services/profit';
import type { ProfitRateInfo } from '../services/profit';
import type { TablePaginationConfig } from 'antd/es/table/interface';

const ProfitRateManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [profitRates, setProfitRates] = useState<ProfitRateInfo[]>([]);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProfitRate, setEditingProfitRate] = useState<ProfitRateInfo | null>(null);
  const [form] = Form.useForm();

  // 获取收益比率列表
  const fetchProfitRates = async (page = 1, pageSize = 10, keyword = '') => {
    setLoading(true);
    try {
      const response = await getProfitRateList({ page, pageSize, keyword });
      console.log('Profit rate list response:', response);
      
      if (response && response.code === 0 && response.data) {
        console.log('Setting profit rates data:', response.data.list);
        setProfitRates(response.data.list);
        setPagination(prev => ({
          ...prev,
          current: page,
          total: response.data.total
        }));
      } else {
        console.error('Invalid response format:', response);
        message.error('获取收益比率列表失败');
      }
    } catch (error) {
      console.error('Error fetching profit rates:', error);
      message.error('获取收益比率列表失败：网络错误');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('ProfitRateManagement component mounted');
    fetchProfitRates(pagination.current, pagination.pageSize);
  }, []);

  // 处理表格变化
  const handleTableChange = (newPagination: TablePaginationConfig) => {
    if (newPagination.current && newPagination.pageSize) {
      fetchProfitRates(newPagination.current, newPagination.pageSize, searchText);
    }
  };

  // 处理搜索
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchProfitRates(1, pagination.pageSize, searchText);
  };

  // 处理重置
  const handleReset = () => {
    setSearchText('');
    setPagination({ ...pagination, current: 1 });
    fetchProfitRates(1, pagination.pageSize);
  };

  // 处理添加/编辑
  const handleAddEdit = async (values: any) => {
    try {
      const params = {
        ...values,
        id: editingProfitRate?.id,
      };
      const response = editingProfitRate
        ? await updateProfitRate(params)
        : await createProfitRate(params);
      
      if (response.code === 0) {
        message.success(editingProfitRate ? '编辑成功' : '添加成功');
        setModalVisible(false);
        form.resetFields();
        fetchProfitRates(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || (editingProfitRate ? '编辑失败' : '添加失败'));
      }
    } catch (error) {
      console.error(editingProfitRate ? '编辑收益比率失败:' : '添加收益比率失败:', error);
      message.error(editingProfitRate ? '编辑失败' : '添加失败');
    }
  };

  // 处理删除
  const handleDelete = async (id: number) => {
    try {
      const response = await deleteProfitRate(id);
      if (response.code === 0) {
        message.success('删除成功');
        fetchProfitRates(pagination.current, pagination.pageSize, searchText);
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  const columns: ColumnsType<ProfitRateInfo> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '最小金额',
      dataIndex: 'min_num',
      key: 'min_num',
      width: 120,
      render: (min_num) => `${min_num} USDT`,
    },
    {
      title: '最大金额',
      dataIndex: 'max_num',
      key: 'max_num',
      width: 120,
      render: (max_num) => `${max_num} USDT`,
    },
    {
      title: '收益比率',
      dataIndex: 'bilv',
      key: 'bilv',
      width: 100,
      render: (bilv) => `${bilv}%`,
    },
    {
      title: '天数',
      dataIndex: 'days',
      key: 'days',
      width: 80,
      render: (days) => `${days}天`,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingProfitRate(record);
              form.setFieldsValue(record);
              setModalVisible(true);
            }}
          >
            编辑
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: '确认删除',
                content: '确定要删除该收益比率吗？',
                onOk: () => handleDelete(record.id),
              });
            }}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Space>
            <Input
              placeholder="搜索金额范围"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
            />
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              搜索
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
          </Space>
        </Col>
        <Col span={12} style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingProfitRate(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            添加收益比率
          </Button>
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={profitRates}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1200 }}
        size="small"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        onChange={handleTableChange}
      />

      <Modal
        title={editingProfitRate ? '编辑收益比率' : '添加收益比率'}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddEdit}
          initialValues={{
            days: 1
          }}
        >
          <Form.Item
            name="min_num"
            label="最小金额"
            rules={[
              { required: true, message: '请输入最小金额' },
              { type: 'number', min: 0, message: '最小金额必须大于0' }
            ]}
          >
            <InputNumber
              placeholder="请输入最小金额"
              min={0}
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
              addonAfter="USDT"
            />
          </Form.Item>

          <Form.Item
            name="max_num"
            label="最大金额"
            rules={[
              { required: true, message: '请输入最大金额' },
              { type: 'number', min: 0, message: '最大金额必须大于0' }
            ]}
          >
            <InputNumber
              placeholder="请输入最大金额"
              min={0}
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
              addonAfter="USDT"
            />
          </Form.Item>

          <Form.Item
            name="bilv"
            label="收益比率"
            rules={[
              { required: true, message: '请输入收益比率' },
              { type: 'number', min: 0, max: 100, message: '收益比率必须在0-100之间' }
            ]}
          >
            <InputNumber
              placeholder="请输入收益比率"
              min={0}
              max={100}
              step={0.01}
              precision={5}
              style={{ width: '100%' }}
              addonAfter="%"
            />
          </Form.Item>

          <Form.Item
            name="days"
            label="天数"
            rules={[
              { required: true, message: '请输入天数' },
              { type: 'number', min: 1, message: '天数必须大于0' }
            ]}
          >
            <InputNumber
              placeholder="请输入天数"
              min={1}
              precision={0}
              style={{ width: '100%' }}
              addonAfter="天"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ProfitRateManagement; 